import React, { FC, ReactNode } from 'react'
import { useBoxes } from '../hooks/useBoxes'
import type { BoxDto } from '../types/generated'
import {
  AuthenticatedUserControllerGetUserVotesParamsOrderByEnum,
  BoxesControllerFindAllParamsOrderByEnum,
  BoxesControllerFindAllParamsOrderDirectionEnum,
  CreatedBoxesMetaDtoOrderByEnum,
  CreatedBoxesMetaDtoOrderDirectionEnum,
} from '../types/generated'
import { BoxStatus } from '../api/boxApi'
import { useUserVotes } from '../hooks/useUserVotes'
import { useCreatedBoxes } from '../hooks/useCreatedBoxes'
import { ScrollBoxes } from './ScrollBoxes'

export const Boxes: FC<{
  titleElement: ReactNode | null
  tabsElement: ReactNode
  status: BoxStatus
  votedByMe?: boolean
  createdByMe?: boolean
  displayFilterFn?: (box: BoxDto) => boolean
}> = ({
  titleElement,
  tabsElement,
  status,
  votedByMe,
  createdByMe,
  displayFilterFn = Boolean,
}) => {
  // User votes (for votedByMe filter)
  const {
    data: userVotesData,
    fetchNextPage: fetchNextUserVotes,
    isFetching: isFetchingUserVotes,
    refetch: refetchUserVotes,
    isFetchingNextPage: isFetchingNextUserVotes,
    isError: isUserVotesError,
  } = useUserVotes({
    orderBy: AuthenticatedUserControllerGetUserVotesParamsOrderByEnum.Activity,
    orderDirection: BoxesControllerFindAllParamsOrderDirectionEnum.Desc,
  })

  // Created boxes (for createdByMe filter)
  const {
    data: createdBoxesData,
    fetchNextPage: fetchNextCreatedBoxes,
    isFetching: isFetchingCreatedBoxes,
    refetch: refetchCreatedBoxes,
    isFetchingNextPage: isFetchingNextCreatedBoxes,
    isError: isCreatedBoxesError,
  } = useCreatedBoxes({
    orderBy: CreatedBoxesMetaDtoOrderByEnum.Deadline,
    orderDirection: CreatedBoxesMetaDtoOrderDirectionEnum.Desc,
  })

  // All boxes (default)
  const {
    data: boxesData,
    fetchNextPage: fetchNextBoxes,
    isFetching: isFetchingBoxes,
    refetch: refetchBoxes,
    isFetchingNextPage: isFetchingNextBoxes,
    isError: isBoxesError,
  } = useBoxes({
    status,
    orderBy: BoxesControllerFindAllParamsOrderByEnum.Deadline,
  })

  if (votedByMe) {
    return (
      <ScrollBoxes
        data={userVotesData}
        fetchNextPage={fetchNextUserVotes}
        isFetching={isFetchingUserVotes}
        isFetchingNextPage={isFetchingNextUserVotes}
        isError={isUserVotesError}
        refetch={refetchUserVotes}
        titleElement={titleElement}
        tabsElement={tabsElement}
        displayFilterFn={displayFilterFn}
      />
    )
  }

  if (createdByMe) {
    return (
      <ScrollBoxes
        data={createdBoxesData}
        fetchNextPage={fetchNextCreatedBoxes}
        isFetching={isFetchingCreatedBoxes}
        isFetchingNextPage={isFetchingNextCreatedBoxes}
        isError={isCreatedBoxesError}
        refetch={refetchCreatedBoxes}
        titleElement={titleElement}
        tabsElement={tabsElement}
        displayFilterFn={displayFilterFn}
      />
    )
  }

  return (
    <ScrollBoxes
      data={boxesData}
      fetchNextPage={fetchNextBoxes}
      isFetching={isFetchingBoxes}
      isFetchingNextPage={isFetchingNextBoxes}
      isError={isBoxesError}
      refetch={refetchBoxes}
      titleElement={titleElement}
      tabsElement={tabsElement}
      displayFilterFn={displayFilterFn}
    />
  )
}
